import { SortOrder } from 'types/interfaces/sort';

export interface SortOption {
  /** Display label shown in the dropdown (without the "Sort: " prefix). */
  label: string;
  /** Backend `sort` query param value. */
  key: string;
  /** Direction auto-applied when this field is selected from the dropdown. */
  defaultDirection: SortOrder;
  /** When true, the option is only valid for English searches (backend is EN-only). */
  enOnly?: boolean;
}

/**
 * Single source of truth for the Search Results "Sort By" dropdown.
 * Order here is the order shown to the user. `name` + `asc` is the
 * backend default (no params sent) and preserves the legacy match-quality ranking.
 */
export const SORT_OPTIONS: readonly SortOption[] = [
  { label: 'Alphabetical', key: 'name', defaultDirection: 'asc' },
  { label: 'Point Value', key: 'point_value', defaultDirection: 'desc', enOnly: true },
  { label: 'Set', key: 'set', defaultDirection: 'desc' },
  { label: 'Release Date', key: 'release_date', defaultDirection: 'desc' },
  { label: 'Rarity', key: 'rarity', defaultDirection: 'desc' },
  { label: 'Mana Value', key: 'mana_value', defaultDirection: 'asc' },
  { label: 'Power', key: 'power', defaultDirection: 'desc' },
  { label: 'Toughness', key: 'toughness', defaultDirection: 'desc' },
] as const;

export const DEFAULT_SORT_KEY = 'name';
export const DEFAULT_SORT_DIRECTION: SortOrder = 'asc';

/** Prefix shown in the dropdown so its purpose reads clearly (e.g. "Sort: Alphabetical"). */
export const SORT_LABEL_PREFIX = 'Sort: ';

export const getDefaultDirection = (key: string): SortOrder =>
  SORT_OPTIONS.find((o) => o.key === key)?.defaultDirection ?? DEFAULT_SORT_DIRECTION;

export const getOptionByKey = (key: string): SortOption | undefined =>
  SORT_OPTIONS.find((o) => o.key === key);
